import { getTrafficSource } from './trafficSource';

/**
 * Captures the user's FIRST-touch traffic source and persists it across the session.
 * Idempotent — only writes on first call per device (first-touch wins).
 *
 * Called once on app mount from AuthContext.
 *
 * Storage key: `first_touch_source` (single key for all entry points — landing, /fatai, /calculators).
 * Kept separate from the legacy `fatai_first_touch_source` key (which is still populated by
 * FatAIPage.tsx for backward compat).
 */
export function captureFirstTouch(): string | null {
  try {
    // First touch wins — never overwrite
    const existing = localStorage.getItem('first_touch_source');
    if (existing) return existing;
    const src = getTrafficSource();
    // Don't pollute storage with internal navigations — only capture on real acquisitions
    if (src === 'internal') return null;
    localStorage.setItem('first_touch_source', src);
    localStorage.setItem('first_touch_at', String(Date.now()));
    localStorage.setItem('first_touch_entry', window.location.pathname || '/');
    return src;
  } catch {
    return null;
  }
}

/**
 * Get the current user's first-touch source (if captured). Returns null if not set.
 */
export function getFirstTouch(): { source: string; at: number; entry: string } | null {
  try {
    const source = localStorage.getItem('first_touch_source');
    if (!source) return null;
    return {
      source,
      at: parseInt(localStorage.getItem('first_touch_at') || '0') || Date.now(),
      entry: localStorage.getItem('first_touch_entry') || '/',
    };
  } catch {
    return null;
  }
}

/**
 * Clear first-touch attribution after successful upload to server.
 */
export function clearFirstTouch() {
  try {
    localStorage.removeItem('first_touch_source');
    localStorage.removeItem('first_touch_at');
    localStorage.removeItem('first_touch_entry');
    // Also clear legacy key
    localStorage.removeItem('fatai_first_touch_source');
    localStorage.removeItem('fatai_first_touch_at');
  } catch { /* noop */ }
}
